import { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import produce from "immer";
import {
  buildPath,
  buildTemplatePath,
  Button,
  ListContainer,
  ListContext,
  Page,
  PageRoute,
  pn,
  SCHOOL_YEAR_ACTIVE_ID,
  SearchBar,
  Stack,
  Typography,
} from "@compass/core";
import {
  LkStudentDisplayFieldId,
  Permission,
  PermissionAction,
  StudentListDto,
  StudentListFilter,
  StudentListResource,
  TenantInfo,
} from "generated/openApi";
import { useInitListFilter, useList } from "hooks";
import { isAuthorized } from "services";
import ApiService from "services/apiService";
import { studentCreatePageRoute } from "../StudentCreatePage/StudentCreatePage";
import { StudentTable } from "./StudentTable";
import { ListFooter } from "components";
import { StudentFilter } from "../StudentFilter";
import { FilterDrawerButton } from "components/common/FilterDrawerButton";
import { FilterDrawer } from "../FilterDrawer";

const routeName = "students";

export const studentListPageRoute: PageRoute = {
  handle: "Student Profiles",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

const ROOT_PERMISSION: Permission = Permission.Student;

const createDefaultListFilter = (schoolIds?: number[], hideArchived = true): StudentListFilter => ({
  sortObjects: [{ property: pn<StudentListDto>("fullName"), isDesc: false }],
  studentFilter: {
    hideArchived,
    schoolYears: [SCHOOL_YEAR_ACTIVE_ID],
    schoolIds: schoolIds?.length ? schoolIds : undefined,
  },
  customFilters: [],
  pageSize: 20,
});

export const StudentListPage = () => {
  const navigate = useNavigate();

  const { listFilter, setListFilter } = useInitListFilter<StudentListFilter>(createDefaultListFilter());

  const [initListFilter, setInitListFilter] = useState<StudentListFilter>(createDefaultListFilter());
  const [listItems, setListItems] = useState<StudentListDto[]>([]);
  const [total, setTotal] = useState<number>();
  const [resource, setResource] = useState<StudentListResource>({});
  const [searchFocused, setSearchFocused] = useState<boolean>(false);
  const [tenantInfo, setTenantInfo] = useState<TenantInfo>();
  const [aiSearchPrompt, setAiSearchPrompt] = useState("");

  // Filter values
  const [filterOpen, setFilterOpen] = useState(false);

  const {
    tableContainerRef,
    applyPage,
    applyPageSize,
    applySortMethod,
    applySort,
    applySearch,
    loadList,
    selectId,
    selectedIds,
    setSelectedIds,
  } = useList({
    listFilter,
    setListFilter,
    fetchList: (listFilter) => {
      ApiService.students.getList(listFilter).then(setListItems);
    },
    fetchTotal: (listFilter) => {
      setTotal(undefined);
      ApiService.students.getListTotal(listFilter).then(setTotal);
    },
  });

  useEffect(() => {
    initializePage();
  }, []);

  const initializePage = async () => {
    ApiService.textingConversations
      .getTenantTextingInfo()
      .then(setTenantInfo);

    ApiService.students
      .getListResource()
      .then(setResource);

    const filterResource = await ApiService.students.getFilterResource();
    const dataAssignedSchoolIds = filterResource.dataAssignedSchoolIds ?? [];
    const archivedDisplayFieldActive = filterResource.displayFields?.some(
      (x) => x.id === LkStudentDisplayFieldId.Archived
    ) ?? false;
    const nextInitListFilter = createDefaultListFilter(dataAssignedSchoolIds, archivedDisplayFieldActive);

    setInitListFilter(nextInitListFilter);
    setListFilter(nextInitListFilter);
    loadList(nextInitListFilter);
  };

  const getIdsMethods = () => ApiService.students.getIds(listFilter);

  const resetListFilter = (nextListFilter = initListFilter) => {
    setAiSearchPrompt("");
    setListFilter(nextListFilter);
    loadList(nextListFilter, { resetPage: true });
  };

  const clearListFilter = (nextListFilter: StudentListFilter) => {
    resetListFilter({
      ...nextListFilter,
      studentFilter: {},
      customFilters: [],
    });
  };

  return (
    <>
      <Page
        header={
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="inherit">
              {studentListPageRoute.handle}
            </Typography>

            <Stack direction="row" alignItems="center" spacing="sm">
              {isAuthorized(ROOT_PERMISSION, PermissionAction.Create) && (
                <Button
                  variant="outlined"
                  onClick={() =>
                    navigate(studentCreatePageRoute.getPath())
                  }
                >
                  + New
                </Button>
              )}
            </Stack>
          </Stack>
        }
      >
        <ListContainer
          fullHeight
          header={
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <SearchBar
                size="small"
                onStoppedTyping={applySearch}
                setSearchFocused={setSearchFocused}
              />

              <FilterDrawerButton
                initListFilter={initListFilter}
                listFilter={listFilter}
                setFilterOpen={setFilterOpen}
                clearFilter={clearListFilter}
                clearToBlank
               />


            </Stack>
          }
          tableContainer={
            <StudentTable
              rootPermission={ROOT_PERMISSION}
              tenantTextingInfo={tenantInfo}
              tableContainerRef={tableContainerRef}
              students={listItems}
              resource={resource}
              listTotal={total}
              sortObjects={listFilter.sortObjects}
              sort={applySort}
              searchFocused={searchFocused}
              selectId={selectId}
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
              getIdsPromise={getIdsMethods}
              loadList={() => loadList(listFilter)}
            />
          }
          footer={
            <ListFooter
              total={total}
              size={listFilter.pageSize}
              page={listFilter.page}
              onPageChange={applyPage}
              onSizeChange={applyPageSize}
              onSortMethodChange={applySortMethod}
            />
          }
        />
      </Page>

      {/* FILTER DRAWER */}
      <FilterDrawer
        listFilter={listFilter}
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        onReset={() => {
          resetListFilter();
        }}
        onApply={() => {
          setListFilter(
            produce(listFilter, (draft) => {
              draft.page = 1;
            })

          );

          loadList(
            produce(listFilter, (draft) => {
              draft.page = 1;
            }),
            { resetPage: true }
          );

          setFilterOpen(false);
        }}
      >
        <StudentFilter
          filter={listFilter || ({} as StudentListFilter)}
          showHideArchived
          setFilter={(filter) =>
            setListFilter(filter)
          }
          aiSearchPrompt={aiSearchPrompt}
          setAiSearchPrompt={setAiSearchPrompt}
        />
      </FilterDrawer>


      {/* LIST CONTEXT */}
      <ListContext.Provider
        value={{
          loadList: (options) =>
            loadList(listFilter, { resetPage: options?.resetPage }),
          clearSelectedIds: () => setSelectedIds([]),
        }}
      >
        <Outlet />
      </ListContext.Provider>
    </>
  );
};
